import type { Family, FamilyChild, FamilyGroup, OwnerParticipant, OwnerWaiting, SessionsLeft } from "@teamna/shared";
import { useTrackNameSearch } from "@teamna/web-client/hooks/use-name-search.ts";
import { useMemo, useState, type MouseEvent } from "react";
import { Avatar, Button, ChevronDownIcon, ConfirmDialog, EmptyState, PeopleIcon, SearchField, SessionsLeftBar } from "../../components/index.ts";
import { DeleteChildText, DeleteWaitingText } from "./delete.tsx";
import { GroupDot, WhatsAppButton } from "./bits.tsx";
import {
  capital,
  childGroupText,
  firstName,
  groupOf,
  participantRows,
  phoneText,
  searchParticipants,
  slotsText,
  staffMessage,
  type ParticipantListRow,
  type Words,
} from "./model.ts";

/**
 * The participants tab (Lana's request, 2026-10-03): every child in one list with a search, for
 * the owner or the coach at the pool. A tap opens everything about the child, a WhatsApp button
 * for each parent with a number, and one for the group's staff member.
 *
 * The names coaches added that wait for a parent (`waiting`) are in the same list with "No parent
 * yet" (pilot feedback, 2026-10-22): at DR Sports Academy most swimmers were such names, and the
 * page showed 23 of about 75. A tap shows who added the name, and Link to a parent opens the page
 * that links them (`onOpenWaiting`).
 *
 * Each open row has Delete, with a question first (Anas, 2026-10-23: a swimmer added two times).
 * A child is "Remove child" of the Families page, with Undo; a waiting name is "Not our swimmer" of
 * the Link page.
 */
export type ParticipantDelete = { kind: "child"; family: Family; child: FamilyChild } | { kind: "waiting"; row: OwnerWaiting };

export function ParticipantsList({
  families,
  groups,
  words,
  waiting = NO_WAITING,
  participants,
  onOpenParent,
  onOpenWaiting,
  onDelete,
}: {
  families: Family[];
  groups: FamilyGroup[];
  words: Words;
  /** The names coaches added that wait for a parent. */
  waiting?: OwnerWaiting[];
  /**
   * Each child's package and sessions left (the participants read, as on the Parents list). Left
   * out, or a child it does not list yet: the row shows no count rather than a wrong one.
   */
  participants?: OwnerParticipant[];
  /** Opens the family's page. Off in Storybook, which has no router. */
  onOpenParent?: (familyId: string) => void;
  /** Opens the page that links the waiting names to parents. */
  onOpenWaiting?: () => void;
  /** Deletes after the question. Off in Storybook. */
  onDelete?: (target: ParticipantDelete) => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<ParticipantDelete | null>(null);
  const [busy, setBusy] = useState(false);
  const deleteName = deleting ? (deleting.kind === "child" ? deleting.child.name : deleting.row.name) : "";
  const confirmDelete = () => {
    if (!deleting || !onDelete || busy) return;
    setBusy(true);
    void onDelete(deleting).finally(() => {
      setBusy(false);
      setDeleting(null);
    });
  };
  const all = useMemo<ParticipantListRow[]>(
    () =>
      [
        ...participantRows(families).map((row) => ({ kind: "child" as const, name: row.child.name, row })),
        ...waiting.map((row) => ({ kind: "waiting" as const, name: row.name, row })),
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [families, waiting],
  );
  const found = useMemo(() => searchParticipants(all, query, groups, words), [all, query, groups, words]);
  const balanceOf = useMemo(() => new Map((participants ?? []).map((participant) => [participant.id, participant])), [participants]);
  const shown = found.rows;
  useTrackNameSearch("families_participants", query, found.results, shown.length);

  return (
    <section aria-label={capital(words.participants)} className="flex flex-col gap-3">
      <SearchField
        label={`Search ${words.participants}`}
        value={query}
        onChange={setQuery}
        placeholder={`Search a ${words.participant}, a ${words.member}, a ${words.group} or a number`}
      />
      <p className="text-[14px] text-ink-2">
        {found.searched
          ? `${shown.length} ${shown.length === 1 ? `${words.participant} matches` : `${words.participants} match`}`
          : `${all.length} ${all.length === 1 ? words.participant : words.participants}. Tap a name to see everything about them.`}
      </p>
      {shown.length === 0 ? (
        <EmptyState
          compact
          icon={<PeopleIcon size={26} />}
          title={all.length === 0 ? `No ${words.participants} yet` : `No ${words.participant} matches`}
          message={all.length === 0 ? "Add families with their children, and they show here." : "Try another name or the last digits of a number."}
        />
      ) : (
        <ul className="overflow-hidden rounded-card bg-paper shadow-card ring-1 ring-line">
          {shown.map((item, index) => {
            if (item.kind === "waiting") {
              return (
                <WaitingItem
                  key={item.row.id}
                  row={item.row}
                  words={words}
                  first={index === 0}
                  isOpen={open === item.row.id}
                  onToggle={() => setOpen(open === item.row.id ? null : item.row.id)}
                  onOpenWaiting={onOpenWaiting}
                  onDelete={onDelete ? () => setDeleting({ kind: "waiting", row: item.row }) : undefined}
                  counted={participants !== undefined}
                />
              );
            }
            const { child, family } = item.row;
            const isOpen = open === child.participantId;
            const group = groupOf(child, groups);
            const facts: Array<[string, string]> = [
              [capital(words.group), group ? `${group.name}${group.staff ? `, ${capital(words.staff)} ${firstName(group.staff.name)}` : ""}` : childGroupText(child, groups, words)],
              ["Sessions", group ? slotsText(group) : `None until a ${words.group} is set`],
              ["Age", child.age !== null ? String(child.age) : "Not given"],
              ["Invite", family.invite === "joined" ? "Joined" : family.invite === "invited" ? "Invited" : "Not invited"],
            ];
            const balance = balanceOf.get(child.participantId);
            if (balance?.packageName) {
              const paid = balance.paymentStatus === "owed" ? ", not paid" : balance.paymentStatus === "paid" ? ", paid" : "";
              facts.push(["Package", `${balance.packageName}${paid}`]);
            }
            if (family.holdNote) facts.push(["On hold", family.holdNote]);
            const panelId = `participant-${child.participantId}`;
            return (
              <li key={child.participantId} className={index === 0 ? "" : "border-t border-line"}>
                <button
                  type="button"
                  onClick={unlessSelecting(() => setOpen(isOpen ? null : child.participantId))}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  className={ROW_CLASS}
                >
                  <Avatar name={child.name} photoUrl={child.photoUrl ?? null} size={36} />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-ink" dir="auto">
                      {child.nameMissing ? `${child.name} (name not set)` : child.name}
                    </span>
                    <span className="block truncate text-[13px] text-ink-2">
                      {capital(words.member)}: {family.adults.map((adult) => adult.name).join(" and ")}
                    </span>
                  </span>
                  <span
                    className={[
                      "inline-flex shrink-0 items-center gap-1.5 rounded-pill px-2.5 py-1 text-[12.5px] font-bold",
                      group ? "bg-sunken text-ink" : "bg-late-surface text-late-ink",
                    ].join(" ")}
                  >
                    <GroupDot color={group?.color ?? null} size={8} />
                    {childGroupText(child, groups, words)}
                  </span>
                  {participants ? <span className={COUNT_SLOT}>{balance?.sessionsLeftFacts ? <SessionsLeftBar facts={balance.sessionsLeftFacts} /> : null}</span> : null}
                  <ChevronDownIcon size={18} className={`shrink-0 text-ink-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen ? (
                  <div id={panelId} className="flex flex-col gap-3 bg-surface px-4 pt-1 pb-4 motion-safe:animate-sheet">
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                      {facts.map(([label, value]) => (
                        <div key={label}>
                          <dt className="text-[12px] font-semibold tracking-[0.02em] text-ink-2 uppercase">{label}</dt>
                          <dd className="text-[14px] text-ink" dir="auto">
                            {value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    {family.adults.map((adult) => (
                      <div key={adult.userId} className="flex flex-wrap items-center gap-3 rounded-xl bg-paper px-3 py-2 ring-1 ring-line">
                        <span className="min-w-0 flex-1">
                          <span className="block font-semibold text-ink" dir="auto">
                            {adult.name}
                          </span>
                          <span className="text-[13px] text-ink-2">{phoneText(adult.phoneE164)}</span>
                        </span>
                        {adult.phoneE164 ? (
                          <WhatsAppButton phoneE164={adult.phoneE164} text={`Hi ${firstName(adult.name)}. About ${child.name}: `}>
                            Message {firstName(adult.name)}
                          </WhatsAppButton>
                        ) : null}
                      </div>
                    ))}
                    {onOpenParent || onDelete ? (
                      <div className="flex flex-wrap gap-2">
                        {onOpenParent ? (
                          <Button variant="secondary" onClick={() => onOpenParent(family.id)}>
                            Open the {words.member}: sessions, payments, settings
                          </Button>
                        ) : null}
                        {onDelete ? (
                          <Button variant="ghost" onClick={() => setDeleting({ kind: "child", family, child })}>
                            Delete
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                    {group?.staff ? (
                      <div className="flex flex-wrap items-center gap-3 rounded-xl bg-paper px-3 py-2 ring-1 ring-line">
                        <Avatar name={group.staff.name} photoUrl={group.staff.photoUrl ?? null} size={32} />
                        <span className="min-w-0 flex-1">
                          <span className="block font-semibold text-ink">
                            {capital(words.staff)} {group.staff.name}
                          </span>
                          <span className="text-[13px] text-ink-2">
                            {group.name}. {phoneText(group.staff.phoneE164)}
                          </span>
                        </span>
                        {group.staff.phoneE164 ? (
                          <WhatsAppButton phoneE164={group.staff.phoneE164} text={staffMessage(child.name, group.name)}>
                            Message the {words.staff}
                          </WhatsAppButton>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      <ConfirmDialog
        open={deleting !== null}
        title={deleting ? `Delete ${deleteName}?` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="danger"
        loading={busy}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      >
        {deleting?.kind === "child" ? <DeleteChildText name={deleteName} words={words} /> : null}
        {deleting?.kind === "waiting" ? <DeleteWaitingText name={deleteName} words={words} /> : null}
      </ConfirmDialog>
    </section>
  );
}

const NO_WAITING: OwnerWaiting[] = [];

/**
 * A row is one button, and the owner can still select a name or a number in it to copy (Anas,
 * 2026-10-23): `select-text` on the button, and a click that ends a text selection does not open or
 * close the row. A key press (`detail` 0) always does.
 */
/**
 * The sessions-left column: the bar of the attendance register (#ai-feedback 2026-10-30), one width
 * on every row so the bars line up after the level tags. A block, so the bar has its full width.
 */
const COUNT_SLOT = "hidden w-[230px] shrink-0 sm:block";

/** A name with no parent has no package: the register's "No package". */
const NO_PACKAGE: SessionsLeft = { kind: "none", size: 0, left: 0, today: "none", paused: false, unpaid: false, unlimited: false };

const ROW_CLASS = "flex min-h-14 w-full select-text items-center gap-3 px-4 py-2.5 text-left hover:bg-surface";
function unlessSelecting(toggle: () => void) {
  return (event: MouseEvent<HTMLButtonElement>) => {
    if (event.detail > 0 && (window.getSelection()?.toString() ?? "") !== "") return;
    toggle();
  };
}

/** A name a coach added, with no parent yet: no sessions and no payments until it is linked. */
function WaitingItem({
  row,
  words,
  first,
  isOpen,
  onToggle,
  onOpenWaiting,
  onDelete,
  counted,
}: {
  row: OwnerWaiting;
  words: Words;
  first: boolean;
  /** The list shows sessions left: a name with no parent has no package yet. */
  counted: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onOpenWaiting?: () => void;
  onDelete?: () => void;
}) {
  const panelId = `waiting-${row.id}`;
  const facts: Array<[string, string]> = [
    [capital(words.group), row.groupName],
    ["Added by", row.addedByName ?? (row.coachNames.join(" and ") || "Not known")],
    [`${capital(words.member)} (from the note)`, [row.noteParentName, row.notePhoneE164 ? phoneText(row.notePhoneE164) : null].filter(Boolean).join(", ") || "Not given"],
  ];
  return (
    <li className={first ? "" : "border-t border-line"}>
      <button
        type="button"
        onClick={unlessSelecting(onToggle)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={ROW_CLASS}
      >
        <Avatar name={row.name} photoUrl={null} size={36} />
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-ink" dir="auto">
            {row.name}
          </span>
          <span className="block truncate text-[13px] font-semibold text-late-ink">No {words.member} yet</span>
        </span>
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-pill bg-sunken px-2.5 py-1 text-[12.5px] font-bold text-ink">
          <GroupDot color={row.groupColor} size={8} />
          {row.groupName}
        </span>
        {counted ? (
          <span className={COUNT_SLOT}>
            <SessionsLeftBar facts={NO_PACKAGE} />
          </span>
        ) : null}
        <ChevronDownIcon size={18} className={`shrink-0 text-ink-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen ? (
        <div id={panelId} className="flex flex-col gap-3 bg-surface px-4 pt-1 pb-4 motion-safe:animate-sheet">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            {facts.map(([label, value]) => (
              <div key={label}>
                <dt className="text-[12px] font-semibold tracking-[0.02em] text-ink-2 uppercase">{label}</dt>
                <dd className="text-[14px] text-ink" dir="auto">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          <p className="text-[14px] text-ink-2">
            Link {firstName(row.name)} to a {words.member}. Then the {words.member} can see the sessions and pay for them.
          </p>
          {onOpenWaiting || onDelete ? (
            <div className="flex flex-wrap gap-2">
              {onOpenWaiting ? (
                <Button variant="secondary" onClick={onOpenWaiting}>
                  Link to a {words.member}
                </Button>
              ) : null}
              {onDelete ? (
                <Button variant="ghost" onClick={onDelete}>
                  Delete
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
